/**
 * Products Service (catalog of products) - list/create/update/delete
 * Mirrors the pricingInputs service but targets the products group.
 */

import { supabase } from '@/lib/supabase'

export interface Product {
  id: string
  unit_id: string
  name: string
  uom: string
  price?: number
  category_id?: string
  group_id?: string
  active: boolean
  attrs?: Record<string, any>
  created_at?: string
  updated_at?: string
}

export interface CreateProductInput {
  name: string
  uom: string
  price?: number
  batch_qty?: number
}

export interface UpdateProductInput {
  name?: string
  uom?: string
  price?: number
  active?: boolean
  attrs?: Record<string, any>
}

interface ProductGroup {
  id: string
  category_id: string | null
}

async function getProductsGroup(): Promise<ProductGroup> {
  const { data: contextData, error: contextError } = await supabase
    .from('contexts')
    .select('id')
    .eq('key', 'pricing')
    .single()

  if (contextError) throw contextError

  const { data: groupData, error: groupError } = await supabase
    .from('asset_groups')
    .select('id, category_id')
    .eq('context_id', contextData.id)
    .eq('key', 'products')
    .maybeSingle()

  if (groupError) throw groupError
  if (!groupData) throw new Error('Products group not found')

  return { id: groupData.id, category_id: groupData.category_id }
}

export async function listProducts(unitId: string): Promise<Product[]> {
  const group = await getProductsGroup()
  const { data, error } = await supabase
    .from('assets')
    .select(`
      id,
      unit_id,
      name,
      uom,
      price,
      category_id,
      group_id,
      active,
      attrs,
      created_at,
      updated_at
    `)
    .eq('unit_id', unitId)
    .eq('active', true)
    .eq('group_id', group.id)
    .order('name', { ascending: true })

  if (error) throw error
  return (data || []) as Product[]
}

export async function createProduct(unitId: string, input: CreateProductInput): Promise<Product> {
  const group = await getProductsGroup()
  const { data, error } = await supabase
    .from('assets')
    .insert([
      {
        unit_id: unitId,
        name: input.name,
        uom: input.uom,
        price: input.price || null,
        category_id: group.category_id,
        group_id: group.id,
        active: true,
        attrs: input.batch_qty ? { batch_qty: input.batch_qty } : {}
      }
    ])
    .select(
      `
      id,
      unit_id,
      name,
      uom,
      price,
      category_id,
      group_id,
      active,
      attrs,
      created_at,
      updated_at
    `
    )
    .single()

  if (error) throw error
  return data as Product
}

export async function updateProduct(id: string, changes: UpdateProductInput): Promise<Product> {
  const { data, error } = await supabase
    .from('assets')
    .update(changes)
    .eq('id', id)
    .select(
      `
      id,
      unit_id,
      name,
      uom,
      price,
      category_id,
      group_id,
      active,
      attrs,
      created_at,
      updated_at
    `
    )
    .single()

  if (error) throw error
  return data as Product
}

export async function deleteProduct(id: string): Promise<void> {
  const { error } = await supabase
    .from('assets')
    .delete()
    .eq('id', id)

  if (error) throw error
}
